import React, { useEffect, useState } from "react";
import { Loader2, Store, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const hdr = () => ({ Authorization: `Bearer ${localStorage.getItem("admin_token")}`, "Content-Type": "application/json" });

export async function pushDishToCassa(dishId, payload) {
  const r = await fetch(`${API}/api/admin/cassa/dishes/${dishId}/push`, { method: "POST", headers: hdr(), body: JSON.stringify(payload) });
  if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.detail || "Errore Cassa in Cloud");
  return r.json();
}

// Box "Cassa in Cloud" dentro l'editor piatto. `value` = { enabled, category_id, department_id, description, label, receipt }
export function CassaDishBox({ dish, nameIt, value, onChange }) {
  const [cat, setCat] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/admin/cassa/catalog?dish_id=${dish.id}`, { headers: hdr() })
      .then((r) => r.json())
      .then((d) => {
        if (!alive) return;
        setCat(d);
        if (!d.configured) return;
        const synced = dish.cassa || d.cassa;
        const isNew = !!dish.created_at && !synced;
        onChange((v) => ({
          ...v,
          enabled: v.enabled ?? (!!synced || isNew),
          category_id: v.category_id || synced?.category_id || d.suggested_category_id || "",
          department_id: v.department_id || synced?.department_id || d.default_department_id || "",
          description: v.description || synced?.description || "",
          label: v.label || synced?.label || "",
          receipt: v.receipt || synced?.receipt || "",
        }));
      })
      .catch(() => alive && setErr("Cassa in Cloud non raggiungibile"));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dish.id]);

  if (cat && !cat.configured) return null;
  const synced = dish.cassa;
  const set = (k, val) => onChange((v) => ({ ...v, [k]: val }));
  const desc = value.description || nameIt;

  return (
    <div className="rounded-xl border border-sky-200 bg-sky-50/50 p-3" data-testid={`dish-cassa-box-${dish.id}`}>
      <div className="flex items-center justify-between gap-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!value.enabled} onChange={(e) => set("enabled", e.target.checked)} className="h-4 w-4 rounded border-sky-300 accent-[#4a1c17]" data-testid={`dish-cassa-toggle-${dish.id}`} />
          <span className="text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] inline-flex items-center gap-1"><Store className="h-3.5 w-3.5 text-sky-700" /> {synced ? "Aggiorna in Cassa in Cloud" : "Invia in Cassa in Cloud"}</span>
        </label>
        {synced ? (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700" data-testid={`dish-cassa-synced-${dish.id}`}><CheckCircle2 className="h-3.5 w-3.5" /> In Cassa · {synced.products?.length || 1} {synced.products?.length === 1 ? "prodotto" : "prodotti"}</span>
        ) : !cat && !err ? <Loader2 className="h-3.5 w-3.5 animate-spin text-sky-700" /> : null}
      </div>
      {err && <div className="mt-1 text-[11px] text-red-700">{err}</div>}
      {value.enabled && cat && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <label className="text-[11px] text-[#7c5a4e]">Categoria Cassa
            <select value={value.category_id} onChange={(e) => set("category_id", e.target.value)} className="mt-1 w-full rounded-md border border-sky-200 bg-white px-2 py-1.5 text-sm" data-testid={`dish-cassa-category-${dish.id}`}>
              <option value="">— scegli —</option>
              {cat.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label className="text-[11px] text-[#7c5a4e]">Reparto IVA
            <select value={value.department_id} onChange={(e) => set("department_id", e.target.value)} className="mt-1 w-full rounded-md border border-sky-200 bg-white px-2 py-1.5 text-sm" data-testid={`dish-cassa-department-${dish.id}`}>
              {cat.departments.map((d) => <option key={d.id} value={d.id}>{d.name}{d.rate != null ? ` · IVA ${d.rate}%` : ""}</option>)}
            </select>
          </label>
          <label className="text-[11px] text-[#7c5a4e] sm:col-span-2">Descrizione (nome prodotto)
            <input value={value.description} onChange={(e) => set("description", e.target.value)} placeholder={nameIt} className="mt-1 w-full rounded-md border border-sky-200 bg-white px-2 py-1.5 text-sm" data-testid={`dish-cassa-description-${dish.id}`} />
          </label>
          <label className="text-[11px] text-[#7c5a4e]">Descrizione sui bottoni
            <input value={value.label} onChange={(e) => set("label", e.target.value)} placeholder={desc} className="mt-1 w-full rounded-md border border-sky-200 bg-white px-2 py-1.5 text-sm" data-testid={`dish-cassa-label-${dish.id}`} />
          </label>
          <label className="text-[11px] text-[#7c5a4e]">Descrizione sullo scontrino
            <input value={value.receipt} onChange={(e) => set("receipt", e.target.value)} placeholder={desc} className="mt-1 w-full rounded-md border border-sky-200 bg-white px-2 py-1.5 text-sm" data-testid={`dish-cassa-receipt-${dish.id}`} />
          </label>
          {(dish.prices?.length || 0) > 1 && <div className="sm:col-span-2 text-[11px] text-[#7c5a4e]">Più prezzi: in Cassa viene creato un prodotto per ciascuno, es. "{desc} ({dish.prices[0].label || "…"})".</div>}
        </div>
      )}
    </div>
  );
}
